import { reactive } from 'vue'
import { realToday } from '../lib/dates'
// The language a first run starts in: the one picked in this browser (on the
// sign-in screen, or in the app before), else the browser's own when OKANE
// speaks it. It is a setting like any other afterwards.
import { preferredLanguage } from '../lib/languages'
import { count } from './usage'

export const DEFAULT_SETTINGS = {
  language: preferredLanguage(),
  currency: 'EUR',
  bank: 'generic',
  openingBalance: 0,
  countUpcomingIncome: true,
  theme: 'dark',
  privacy: 'off', // off | on | strict — see stores/settings.js
  onboarded: false,
  // What the duplicates sheet counts as "the same payment twice".
  duplicateSimilarity: 50, // percent alike, 0–100
  duplicateDays: 7, // at most this many days apart
  duplicateAmount: 0, // percent the amounts may differ by, of the larger; 0 is to the cent
}

/** One reactive tree for the whole app; SQLite stays the source of truth. */
export const state = reactive({
  ready: false,
  // What day it is, as the app sees it. Kept current by stores/clock.js and
  // read by lib/dates.js's today(), so anything that depends on the date is
  // redrawn when it changes — a new day, a new period, a new month on screen.
  today: realToday(),
  // Where the database is kept and whether the browser has promised to keep
  // it: `{ backend: 'opfs' | 'indexeddb' | 'native', moved, persisted }`.
  storage: { backend: null, moved: false, persisted: null },
  // Cloud mode only: who is signed in, the org whose ledger is open (with this
  // account's role in it), and the socket's state.
  cloud: { account: null, org: null, connection: 'local', encrypting: false },
  // Sync mode (stores/sync.js): this device's ledger, end-to-end encrypted
  // through a vault on the server. `status`: off | connecting | syncing |
  // synced | offline | locked (waiting for the key) | schema-ahead | error.
  // `outbox`: attached files sealed here and not sent yet (stores/attachments.js).
  sync: { on: false, gate: false, org: null, role: null, status: 'off', code: null, waiting: [], conflicts: 0, editing: null, error: null, outbox: 0, outboxError: null },
  // Bumped by every write to the ledger (stores/live.js): each value the
  // database works out is asked again. The transactions themselves are never
  // all loaded — the list reads them a page at a time into `ledger`.
  dataVersion: 0,
  // The list: the rows read so far for the current filters and sort, whether
  // there are more, and how many it holds when a write asks for them again.
  ledger: { rows: [], done: false, loading: false },
  plans: [],
  budgets: [],
  goals: [],
  tags: [],
  categories: [],
  categoryTags: {}, // category id -> tag ids worn by everything filed under it
  settings: { ...DEFAULT_SETTINGS },
  // What has been earned: `{ id, earned_on }` rows (stores/achievements.js).
  achievements: [],
  ui: {
    search: '',
    // Every filter is a list: any of the values shows a row, and the lists
    // narrow each other. Empty means "not filtering by this".
    kinds: [], // income | expense
    status: [], // pending | settled — what the bank has finished with
    files: [], // attached | unattached — whether files are attached to it
    tags: [], // tag ids, or NONE for untagged
    categories: [], // category names, or NONE for uncategorised
    from: null, // YYYY-MM-DD, the earliest day shown
    to: null, // YYYY-MM-DD, the latest
    amountFrom: null, // the smallest size shown, ignoring which way it went
    amountTo: null, // and the largest
    sort: { by: 'date', dir: 'desc' }, // date | description | amount
    panel: 'transactions', // small screens only: transactions | plan | goals
    chartsExpanded: false, // the Trends row, grown in place
    chartGrain: 'month', // day | month — what a bucket is, when the window spans more than one
    chartAll: true, // the everything series: balance, and money in and out
    // Lines drawn beside the everything series, by slot: `{ kind, id }` where
    // kind is 'tag' or 'category'. The slot is the identity, so a line keeps
    // its colour when another is turned off beside it.
    chartSeries: [null, null, null, null, null, null, null, null],
    selecting: false, // picking rows for a bulk action
    selected: [], // transaction ids, while selecting
    sheet: null, // { name, payload }
    rangeOpen: false, // the date range popover in the header (RangePicker)
    toast: null,
  },
})

export const openSheet = (name, payload = null) => {
  count('sheet', name)
  state.ui.sheet = { name, payload }
}
export const closeSheet = () => {
  state.ui.sheet = null
}

let toastTimer = null
export function toast(message, tone = 'info') {
  state.ui.toast = { message, tone, id: Date.now() }
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => (state.ui.toast = null), 3200)
}

export function toggleSelected(id) {
  const at = state.ui.selected.indexOf(id)
  if (at > -1) state.ui.selected.splice(at, 1)
  else state.ui.selected.push(id)
}

export function stopSelecting() {
  state.ui.selecting = false
  state.ui.selected = []
}
